/**
 * V1.5 Add-Auftrag-Modal (Brief §551-555).
 *
 * Holt einen Auftrag aus einem anderen Projekt ins aktuelle Projekt. Quelle wird bei Leere
 * zusammengeführt (#123), gesperrte Quellen ausgegraut mit Grund (#112). Vor dem Verschieben
 * fragt ein ConfirmDialog nach — sein Text unterscheidet „Quelle wird zusammengeführt" von
 * „nur der Auftrag wandert". SF-Suche nach Bestellnummer/Opportunity (Brief §317) kommt in
 * einer späteren Iteration.
 */
import type { AuftragSperrgrund } from '@projektverwaltung/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { useToast } from '../toast.js';
import { BEFUNDE_QUERY_KEY } from './befunde/befund-basis.js';
import { useConfirm } from './confirm-dialog.js';
import { Modal } from './modal.js';

type MovableItem = {
  auftrag_id: string;
  project_key: string;
  name: string;
  customer_name: string;
  segment: string;
  kind: 'auftrag' | 'cr';
  ms_classification: string | null;
  sf_budget_eur: string | null;
  source_project_id: string;
  source_project_key: string;
  source_member_count: number;
  sperrgruende: AuftragSperrgrund[];
};

export function AddAuftragModal({
  open,
  onClose,
  targetProjectId,
}: {
  open: boolean;
  onClose: () => void;
  targetProjectId: string;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const confirmAction = useConfirm();
  const [searchInput, setSearchInput] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [selected, setSelected] = useState<MovableItem | null>(null);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQ(searchInput.trim()), 200);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    if (!open) {
      setSearchInput('');
      setDebouncedQ('');
      setSelected(null);
    }
  }, [open]);

  const listQuery = useQuery({
    queryKey: ['v2-auftraege-movable', targetProjectId, debouncedQ],
    queryFn: () =>
      api.listMovableAuftraege({
        excludeProjectId: targetProjectId,
        ...(debouncedQ ? { q: debouncedQ } : {}),
      }),
    enabled: open,
  });

  const attachMut = useMutation({
    mutationFn: (item: MovableItem) => api.attachAuftrag(targetProjectId, item.auftrag_id),
    onSuccess: (res, item) => {
      toast.success(
        res.source_merged
          ? t(
              'v2.zusammenfuehren.toast_done_merged',
              'Auftrag verschoben — {{quelle}} ist jetzt zusammengeführt.',
              { quelle: item.source_project_key },
            )
          : t('v2.add_auftrag.toast_done', 'Auftrag verschoben.'),
      );
      void queryClient.invalidateQueries({ queryKey: ['v2-project-auftraege', targetProjectId] });
      void queryClient.invalidateQueries({ queryKey: ['v2-project', targetProjectId] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['v2-auftraege-movable'] });
      // PV-052 L2: Der Server hat abgewartet neu geprüft — Befunde frisch laden.
      void queryClient.invalidateQueries({ queryKey: BEFUNDE_QUERY_KEY });
      onClose();
    },
    onError: (err) =>
      toast.error(
        t('v2.add_auftrag.toast_failed', 'Verschieben fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  async function verschieben(item: MovableItem): Promise<void> {
    // Fall a: die Quelle hat nur diesen einen Auftrag → sie wird zusammengeführt.
    const fallA = item.source_member_count <= 1;
    const ok = await confirmAction({
      title: t('v2.zusammenfuehren.confirm_title', 'Auftrag {{auftrag}} nach hier verschieben?', {
        auftrag: item.project_key,
      }),
      body: fallA
        ? t('v2.zusammenfuehren.confirm_body_merge', '', { quelle: item.source_project_key })
        : t('v2.zusammenfuehren.confirm_body_teil', '', { quelle: item.source_project_key }),
      confirmLabel: t('v2.zusammenfuehren.confirm_label', 'Verschieben'),
    });
    if (ok) attachMut.mutate(item);
  }

  const items = listQuery.data?.items ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      labelledBy="add-auftrag-title"
      panelClassName="w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden"
    >
      <div className="p-4 border-b" style={{ borderColor: 'var(--line-soft)' }}>
        <h2
          id="add-auftrag-title"
          className="text-base font-medium"
          style={{ color: 'var(--ink)', fontFamily: 'var(--f-display)' }}
        >
          {t('v2.add_auftrag.title', 'Auftrag hinzufügen')}
        </h2>
        <p className="text-xs mt-1" style={{ color: 'var(--ink-3)' }}>
          {t(
            'v2.add_auftrag.hint',
            'Wählt einen Auftrag aus einem anderen Projekt aus. SF-Suche nach Bestellnummer/Opportunity kommt später.',
          )}
        </p>
      </div>
      <div className="p-4 border-b" style={{ borderColor: 'var(--line-soft)' }}>
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t('v2.add_auftrag.search_placeholder', 'Suche nach Name, P-Key, Kunde…')}
          className="w-full text-sm px-3 py-2 rounded-md outline-none transition-colors"
          style={{
            background: 'var(--surface)',
            color: 'var(--ink)',
            border: '1px solid var(--line-soft)',
          }}
          // Kein autoFocus nötig: <Modal> setzt den Fokus beim Öffnen aufs erste bedienbare
          // Element — das ist dieses Suchfeld.
        />
      </div>
      <div className="flex-1 overflow-y-auto">
        {listQuery.isLoading && (
          <p className="p-4 text-sm" style={{ color: 'var(--ink-3)' }}>
            {t('v2.add_auftrag.loading', 'Lädt…')}
          </p>
        )}
        {!listQuery.isLoading && items.length === 0 && (
          <p className="p-4 text-sm" style={{ color: 'var(--ink-3)' }}>
            {t('v2.add_auftrag.empty', 'Keine passenden Aufträge gefunden.')}
          </p>
        )}
        {!listQuery.isLoading && items.length > 0 && (
          <ul className="divide-y" style={{ borderColor: 'var(--line-soft)' }}>
            {items.map((item) => {
              const isSelected = selected?.auftrag_id === item.auftrag_id;
              const gesperrt = item.sperrgruende.length > 0;
              const grundId = `add-auftrag-grund-${item.auftrag_id}`;
              return (
                <li
                  key={item.auftrag_id}
                  style={{
                    background: isSelected ? 'var(--primary-soft)' : 'transparent',
                    borderColor: 'var(--line-soft)',
                    opacity: gesperrt ? 0.55 : 1,
                  }}
                >
                  {/* Echter Knopf: per Tab erreichbar, Enter/Leertaste wählen aus. Gesperrte Zeilen
                      bleiben bewusst fokussierbar (aria-disabled statt disabled), damit ein
                      Screenreader den Grund vorliest (aria-describedby auf die Grundliste). */}
                  <button
                    type="button"
                    aria-pressed={isSelected}
                    aria-disabled={gesperrt}
                    aria-describedby={gesperrt ? grundId : undefined}
                    onClick={gesperrt ? undefined : () => setSelected(item)}
                    className={`block w-full text-left px-3 pt-3 ${gesperrt ? 'pb-1 cursor-not-allowed' : 'pb-3 cursor-pointer'}`}
                    style={{ background: 'transparent', border: 'none' }}
                  >
                    <span className="flex items-center gap-2 mb-0.5">
                      <span
                        style={{ fontFamily: 'var(--f-mono)', fontSize: 12, color: 'var(--ink-2)' }}
                      >
                        {item.project_key}
                      </span>
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded uppercase"
                        style={{
                          background:
                            item.kind === 'cr' ? 'var(--primary-soft)' : 'var(--success-soft)',
                          color: item.kind === 'cr' ? 'var(--primary)' : 'var(--success)',
                        }}
                      >
                        {item.kind}
                      </span>
                      <span className="text-[10px]" style={{ color: 'var(--ink-3)' }}>
                        · {item.segment}
                      </span>
                      {gesperrt && (
                        <span
                          className="text-[10px] ml-auto px-1.5 py-0.5 rounded"
                          style={{ background: 'var(--surface-2)', color: 'var(--ink-3)' }}
                        >
                          {t('v2.zusammenfuehren.gesperrt_badge', 'gesperrt')}
                        </span>
                      )}
                    </span>
                    <span className="block text-sm" style={{ color: 'var(--ink)' }}>
                      {item.name}
                    </span>
                    <span className="block text-xs" style={{ color: 'var(--ink-3)' }}>
                      {item.customer_name} · aus {item.source_project_key}
                    </span>
                  </button>
                  {gesperrt && (
                    <ul id={grundId} className="text-xs px-3 pb-3" style={{ color: 'var(--warn)' }}>
                      {item.sperrgruende.map((g) => (
                        <li key={g}>
                          {t(`v2.zusammenfuehren.sperrgrund.${g}`)}
                          {/* #142: Für diesen Grund gibt es nur einen Ausweg — direkt dazuschreiben,
                              gleichlautend mit der 409-Meldung (sperre.ts herkunftKonfliktAusweg). */}
                          {g === 'herkunft_konflikt' && (
                            <span className="block">
                              {t('v2.zusammenfuehren.sperrgrund.herkunft_konflikt_ausweg', {
                                projekt: item.source_project_key,
                                auftrag: item.project_key,
                              })}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div
        className="p-3 flex items-center justify-end gap-2 border-t"
        style={{ borderColor: 'var(--line-soft)' }}
      >
        <button
          type="button"
          onClick={onClose}
          className="text-xs px-3 py-1.5 rounded-md"
          style={{
            background: 'var(--surface-2)',
            color: 'var(--ink-2)',
            border: '1px solid var(--line-soft)',
          }}
        >
          {t('v2.modal.cancel', 'Abbrechen')}
        </button>
        <button
          type="button"
          onClick={() => selected && void verschieben(selected)}
          disabled={!selected || attachMut.isPending}
          className="text-xs px-3 py-1.5 rounded-md"
          style={{
            background: 'var(--primary)',
            color: 'var(--on-primary)',
            border: 'none',
            opacity: !selected || attachMut.isPending ? 0.5 : 1,
            cursor: !selected || attachMut.isPending ? 'not-allowed' : 'pointer',
          }}
        >
          {attachMut.isPending
            ? t('v2.add_auftrag.saving', 'Verschiebt…')
            : t('v2.add_auftrag.submit', 'Verschieben →')}
        </button>
      </div>
    </Modal>
  );
}
